/**
 * Page d'atterrissage PUBLIQUE d'un lien d'invitation tuteur (migration 0241).
 * URL : /rejoindre/$token — accessible sans compte (route top-level, PAS sous
 * `appRoute`, cf. router.tsx : `appRoute`/`onboardingRoute` redirigent tous
 * deux sans préserver aucun paramètre d'URL, cf. CLAUDE.md).
 *
 * Calque `bienvenue.tsx` (contexte public par token + OtpAuthForm) croisé avec
 * `_app.lier-parent.$code.tsx` (cartes succès/erreur).
 */

import { useEffect, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CompassLogo,
  Alert,
  AlertDescription,
} from '@jeraisonne/ui';
import { CheckCircle2 } from 'lucide-react';
import { OtpAuthForm } from '@/components/otp-auth-form';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import {
  fetchTutorInvitePublic,
  redeemTutorInviteLink,
  translateInviteError,
  type TutorInvitePublicContext,
} from '@/lib/tutor-invite';

export function JoinTutorInvitePage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const { session, profile, profileLoading } = useAuth();

  const [context, setContext] = useState<TutorInvitePublicContext | null>(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [contextError, setContextError] = useState<string | null>(null);

  const [redeemState, setRedeemState] = useState<'idle' | 'pending' | 'done' | 'error'>('idle');
  const [redeemResult, setRedeemResult] = useState<{ alreadyLinked: boolean } | null>(null);
  const [redeemError, setRedeemError] = useState<string | null>(null);

  // Contexte public (nom du tuteur + mode) — pas de session requise.
  useEffect(() => {
    let cancelled = false;
    fetchTutorInvitePublic(token)
      .then((ctx) => {
        if (!cancelled) setContext(ctx);
      })
      .catch((err) => {
        if (!cancelled)
          setContextError(translateInviteError(err instanceof Error ? err.message : ''));
      })
      .finally(() => {
        if (!cancelled) setContextLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Nouveau compte (session posée, mais pas encore de profil) → direction
  // onboarding, qui pré-sélectionne le rôle et lie le compte en fin d'étape.
  useEffect(() => {
    if (!session || profileLoading || profile) return;
    window.location.href = `/onboarding?invite=${encodeURIComponent(token)}`;
  }, [session, profileLoading, profile, token]);

  // Compte élève déjà existant : redemption directe (onboarding ne se
  // relancera jamais pour un profil déjà créé, cf. onboardingRoute.beforeLoad).
  useEffect(() => {
    if (!session || profileLoading || !profile) return;
    if (profile.role !== 'student' || redeemState !== 'idle') return;
    setRedeemState('pending');
    (async () => {
      // Onboarding abandonné à l'étape « profil » : `profiles` existe mais pas
      // encore `student_profiles`. Consommer le lien maintenant échouerait
      // (« réservée aux alternants ») — on reprend l'onboarding, où le statut
      // (étudiant alternant) est verrouillé par le lien puis le rattachement fait.
      const { data: studentProfile } = await supabase
        .from('student_profiles')
        .select('id')
        .eq('id', session.user.id)
        .maybeSingle();
      if (!studentProfile) {
        window.location.href = `/onboarding?invite=${encodeURIComponent(token)}`;
        return;
      }
      const res = await redeemTutorInviteLink(token);
      setRedeemResult({ alreadyLinked: res.alreadyLinked });
      setRedeemState('done');
    })().catch((err) => {
      setRedeemError(translateInviteError(err instanceof Error ? err.message : ''));
      setRedeemState('error');
    });
  }, [session, profileLoading, profile, token, redeemState]);

  const homeTo = '/student/dashboard';

  return (
    <div className="min-h-dvh bg-transparent">
      <header className="flex justify-center px-6 py-8">
        <CompassLogo size="lg" wordmarkClassName="text-ink-900" />
      </header>

      <main className="mx-auto flex w-full max-w-md flex-col px-6 pb-16 pt-4 sm:pt-12">
        <Card className="border-ink-100/60 shadow-lg">
          {contextLoading ? (
            <CardHeader>
              <div className="flex justify-center py-10">
                <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
              </div>
            </CardHeader>
          ) : contextError ? (
            <CardContent className="space-y-4 py-6 text-center">
              <p className="text-ink-900 text-lg font-semibold">Lien invalide</p>
              <p className="text-ink-500 text-sm">{contextError}</p>
              <Button asChild className="w-full">
                <a href="/login">Aller à la connexion</a>
              </Button>
            </CardContent>
          ) : !session ? (
            <OtpAuthForm
              initialMode="signup"
              renderHeader={({ step }) =>
                step === 'otp' ? (
                  <>
                    <CardTitle>Vérifie ta boîte mail</CardTitle>
                    <CardDescription>
                      Entre le code à 6 chiffres qu'on vient d'envoyer.
                    </CardDescription>
                  </>
                ) : (
                  <>
                    <CardTitle>
                      {context?.tutorDisplayName} t'invite sur Jeraisonne{' '}
                      <span aria-hidden="true">🎉</span>
                    </CardTitle>
                    <CardDescription>
                      {context?.kind === 'academic'
                        ? "Rejoins-le en tant qu'étudiant·e en alternance pour tes entretiens académiques."
                        : 'Crée ton compte (ou connecte-toi) pour être automatiquement relié·e à ton prof.'}
                    </CardDescription>
                  </>
                )
              }
            />
          ) : profileLoading ||
            (profile &&
              profile.role === 'student' &&
              redeemState !== 'done' &&
              redeemState !== 'error') ? (
            <CardHeader>
              <div className="flex flex-col items-center gap-4 py-6 text-center">
                <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
                <CardDescription>Un instant, on relie ton compte…</CardDescription>
              </div>
            </CardHeader>
          ) : !profile ? (
            // Session posée, profil pas encore créé → l'effet ci-dessus redirige
            // déjà vers /onboarding ; on affiche un état transitoire cohérent.
            <CardHeader>
              <div className="flex justify-center py-10">
                <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
              </div>
            </CardHeader>
          ) : profile.role !== 'student' ? (
            <CardContent className="space-y-4 py-6 text-center">
              <p className="text-ink-900 text-lg font-semibold">Lien réservé aux élèves</p>
              <p className="text-ink-500 text-sm">
                Ton compte existant ({profile.role === 'tutor' ? 'professeur' : profile.role}) ne
                peut pas être relié via ce lien.
              </p>
              <Button asChild className="w-full">
                <a href="/">Retour à l'accueil</a>
              </Button>
            </CardContent>
          ) : redeemState === 'error' ? (
            <CardContent className="space-y-4 py-6 text-center">
              <p className="text-ink-900 text-lg font-semibold">Impossible de te relier</p>
              <Alert variant="error">
                <AlertDescription>{redeemError}</AlertDescription>
              </Alert>
              <Button asChild className="w-full">
                <a href={homeTo}>Aller à mon tableau de bord</a>
              </Button>
            </CardContent>
          ) : (
            <CardContent className="space-y-5 py-6 text-center">
              <div className="flex flex-col items-center">
                <CheckCircle2 className="mb-2 h-12 w-12 text-emerald-500" />
                <p className="text-ink-900 text-lg font-semibold">
                  {redeemResult?.alreadyLinked ? 'Déjà relié·e !' : 'Compte relié !'}
                </p>
                <p className="text-ink-500 mt-1 text-sm">
                  Tu es maintenant relié·e à <strong>{context?.tutorDisplayName}</strong>.
                </p>
              </div>
              <Button asChild className="w-full">
                <a href={homeTo}>Aller à mon tableau de bord</a>
              </Button>
            </CardContent>
          )}
        </Card>
      </main>
    </div>
  );
}
